import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Snackbar,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { Add, Close, Search, TableRows, ViewKanbanOutlined, WbSunnyOutlined } from "@mui/icons-material";
import { KeyHint } from "../../components/common/KeyHint";
import { TaskPriority, TaskStatus } from "../../services/taskService";
import { TaskStoreProvider, useTaskStore } from "./TaskContext";
import {
    EMPTY_FILTERS,
    filterTasks,
    hasActiveFilters,
    myTaskSections,
    personName,
    PRIORITIES,
    PRIORITY_META,
    QUICK_FILTERS,
    QuickFilter,
    TaskFilters,
} from "./taskUtils";
import { MyTasks } from "./components/MyTasks";
import { GROUP_OPTIONS, groupTasks, GroupBy, SortBy, TaskList, visibleRows } from "./components/TaskList";
import { boardOrder, TaskBoard } from "./components/TaskBoard";
import { TaskDrawer } from "./components/TaskDrawer";
import { QuickAdd, QuickAddDefaults, QuickAddDialog } from "./components/QuickAdd";
import { useRecordViewer } from "./components/LinkedRecords";
import { StaffAvatar } from "./components/TaskBits";

type View = "my" | "board" | "list";

const DONE_COLLAPSED_KEY = "tasks.board.doneCollapsed";

const readFlag = (key: string) => {
    try {
        return localStorage.getItem(key) === "1";
    } catch {
        return false;
    }
};
const writeFlag = (key: string, on: boolean) => {
    try {
        localStorage.setItem(key, on ? "1" : "0");
    } catch {
        // per-viewer convenience only
    }
};

const isTyping = (target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
};

export const TasksPage: React.FC = () => (
    <TaskStoreProvider>
        <TasksPageInner />
    </TaskStoreProvider>
);

const TasksPageInner: React.FC = () => {
    const store = useTaskStore();
    const { tasks, today, loading, error, people, peopleById, meId, labels, upsert, lastToast, clearToast } = store;
    const [params, setParams] = useSearchParams();
    const view = (["my", "board", "list"].includes(params.get("view") || "") ? params.get("view") : "my") as View;
    const openTaskId = params.get("task");
    const groupBy = (params.get("group") as GroupBy) || "status";
    const sortBy = (params.get("sort") as SortBy) || "due";
    const byPerson = params.get("lanes") === "person";
    const filters: TaskFilters = {
        q: params.get("q") || "",
        quick: (params.get("quick") as QuickFilter) || null,
        person: params.get("person"),
        priority: (params.get("priority") as TaskPriority) || null,
        label: params.get("label"),
    };

    const [activeId, setActiveId] = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set(["done"]));
    const [doneCollapsed, setDoneCollapsed] = useState(() => readFlag(DONE_COLLAPSED_KEY));
    const [dialog, setDialog] = useState<QuickAddDefaults | null>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const quickAddRef = useRef<HTMLDivElement>(null);
    const viewer = useRecordViewer((message) => store.toast({ message, severity: "error" }));

    const setParam = useCallback(
        (changes: Record<string, string | null>, replace = true) => {
            setParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
                    return next;
                },
                { replace },
            );
        },
        [setParams],
    );
    const setFilters = (changes: Partial<TaskFilters>) => {
        const next = { ...filters, ...changes };
        setParam({ q: next.q || null, quick: next.quick, person: next.person, priority: next.priority, label: next.label });
    };
    const openTask = useCallback((id: string | null) => setParam({ task: id }, false), [setParam]);

    // ── What each view shows ───────────────────────────────────────────────
    const filtered = useMemo(
        () => filterTasks(tasks, view === "my" ? { ...EMPTY_FILTERS, q: filters.q } : filters, meId, today, peopleById),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [tasks, view, filters.q, filters.quick, filters.person, filters.priority, filters.label, meId, today, peopleById],
    );
    const sections = useMemo(() => myTaskSections(filtered, meId, today), [filtered, meId, today]);
    const groups = useMemo(() => groupTasks(filtered, groupBy, sortBy, today, peopleById), [filtered, groupBy, sortBy, today, peopleById]);

    const ordered = useMemo(() => {
        if (view === "my") return sections.filter((s) => !collapsed.has(s.key)).flatMap((s) => s.tasks);
        if (view === "list") return visibleRows(groups, collapsed);
        return boardOrder(filtered, byPerson, doneCollapsed, peopleById);
    }, [view, sections, groups, collapsed, filtered, byPerson, doneCollapsed, peopleById]);
    const orderedIds = ordered.map((t) => t.task_id);
    const openIndex = openTaskId ? orderedIds.indexOf(openTaskId) : -1;

    useEffect(() => {
        if (openTaskId) setActiveId(openTaskId);
    }, [openTaskId]);

    // ── Keyboard ───────────────────────────────────────────────────────────
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
            if (openTaskId || dialog || document.querySelector(".MuiModal-root:not(.MuiPopover-root)")) return;
            if (e.key === "n" || e.key === "N") {
                e.preventDefault();
                quickAddRef.current?.querySelector("input")?.focus();
            } else if (e.key === "/") {
                e.preventDefault();
                if (view === "my") quickAddRef.current?.querySelector("input")?.focus();
                else searchRef.current?.focus();
            } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                if (!orderedIds.length) return;
                e.preventDefault();
                const i = activeId ? orderedIds.indexOf(activeId) : -1;
                const next = e.key === "ArrowDown" ? Math.min(i + 1, orderedIds.length - 1) : Math.max(i - 1, 0);
                setActiveId(orderedIds[i === -1 ? 0 : next]);
            } else if (e.key === "Enter" && activeId && orderedIds.includes(activeId)) {
                e.preventDefault();
                openTask(activeId);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [orderedIds, activeId, openTaskId, dialog, view, openTask]);

    const toggleCollapsed = (key: string) =>
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const onCreated = (task: Parameters<typeof upsert>[0]) => {
        upsert(task);
        const mine = task.assignee_user_id === meId;
        store.toast({
            message: view === "my" && !mine ? "Task added — it's on their list, not yours." : "Task added.",
            severity: "success",
            action: { label: "Open", onClick: () => openTask(task.task_id) },
        });
        store.reload(true);
    };

    // ── Header numbers ─────────────────────────────────────────────────────
    const me = meId ? peopleById.get(meId) : undefined;
    const mineOpen = tasks.filter((t) => t.assignee_user_id === meId && t.status !== "DONE");
    const overdueMine = mineOpen.filter((t) => t.due_date && t.due_date < today).length;
    const todayMine = mineOpen.filter((t) => t.due_date === today).length;
    const team = people.filter((p) => p.assignable);
    const filtersOn = view !== "my" && hasActiveFilters(filters);

    return (
        <Box sx={{ maxWidth: view === "my" ? 900 : "none", mx: "auto" }}>
            {/* Title row */}
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "#10282c" }}>
                        Tasks
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {loading && !tasks.length
                            ? "Loading…"
                            : mineOpen.length === 0
                              ? `Nothing on your list${me ? `, ${personName(me).split(" ")[0]}` : ""}.`
                              : [
                                    `${mineOpen.length} open for you`,
                                    todayMine ? `${todayMine} due today` : null,
                                    overdueMine ? `${overdueMine} overdue` : null,
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={view}
                    onChange={(_, v) => v && setParam({ view: v === "my" ? null : v })}
                    sx={{ "& .MuiToggleButton-root": { textTransform: "none", px: 1.5, gap: 0.75, fontWeight: 600 } }}
                >
                    <ToggleButton value="my">
                        <WbSunnyOutlined fontSize="small" /> My tasks
                    </ToggleButton>
                    <ToggleButton value="board">
                        <ViewKanbanOutlined fontSize="small" /> Board
                    </ToggleButton>
                    <ToggleButton value="list">
                        <TableRows fontSize="small" /> List
                    </ToggleButton>
                </ToggleButtonGroup>
                <Button variant="contained" startIcon={<Add />} onClick={() => setDialog({ assigneeId: view === "my" ? meId : null })} sx={{ whiteSpace: "nowrap" }}>
                    New task
                    <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        <KeyHint>N</KeyHint>
                    </Box>
                </Button>
            </Stack>

            {/* Quick add */}
            <Box ref={quickAddRef} sx={{ mb: 2 }}>
                <QuickAdd
                    variant="bar"
                    defaults={{ assigneeId: view === "my" ? meId : null }}
                    onCreated={onCreated}
                    placeholder={
                        view === "my"
                            ? "Add to your list… e.g. Call customer tomorrow 4pm !high (or @Name for someone else)"
                            : "Add a task… e.g. Pack ORD202610260013 @Rina today !urgent #delivery"
                    }
                />
            </Box>

            {/* Filters (board & list) */}
            {view !== "my" && (
                <Stack spacing={1.25} sx={{ mb: 2 }}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} alignItems="center">
                        <TextField
                            inputRef={searchRef}
                            size="small"
                            placeholder="Search tasks"
                            value={filters.q}
                            onChange={(e) => setFilters({ q: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                    e.stopPropagation();
                                    if (filters.q) setFilters({ q: "" });
                                    else (e.target as HTMLElement).blur();
                                }
                            }}
                            sx={{ width: { xs: "100%", sm: 240 } }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: <KeyHint>/</KeyHint>,
                                    sx: { borderRadius: 2, bgcolor: "background.paper" },
                                },
                            }}
                        />
                        {QUICK_FILTERS.map((f) => (
                            <Chip
                                key={f.key}
                                label={f.label}
                                onClick={() => setFilters({ quick: filters.quick === f.key ? null : f.key })}
                                color={filters.quick === f.key ? "primary" : "default"}
                                variant={filters.quick === f.key ? "filled" : "outlined"}
                                sx={{ fontWeight: 600 }}
                            />
                        ))}
                        <TextField
                            select
                            size="small"
                            value={filters.priority || ""}
                            onChange={(e) => setFilters({ priority: (e.target.value as TaskPriority) || null })}
                            sx={{ minWidth: 130 }}
                            slotProps={{ select: { displayEmpty: true }, input: { sx: { borderRadius: 2, bgcolor: "background.paper" } } }}
                        >
                            <MenuItem value="">Any priority</MenuItem>
                            {PRIORITIES.map((p) => (
                                <MenuItem key={p} value={p}>
                                    {PRIORITY_META[p].label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {labels.length > 0 && (
                            <TextField
                                select
                                size="small"
                                value={filters.label || ""}
                                onChange={(e) => setFilters({ label: e.target.value || null })}
                                sx={{ minWidth: 130 }}
                                slotProps={{ select: { displayEmpty: true }, input: { sx: { borderRadius: 2, bgcolor: "background.paper" } } }}
                            >
                                <MenuItem value="">Any label</MenuItem>
                                {labels.map((l) => (
                                    <MenuItem key={l} value={l}>
                                        #{l}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        {filtersOn && (
                            <Button size="small" startIcon={<Close />} onClick={() => setFilters(EMPTY_FILTERS)} sx={{ textTransform: "none" }}>
                                Clear
                            </Button>
                        )}
                        <Box sx={{ flex: 1 }} />
                        {view === "board" ? (
                            <FormControlLabel
                                control={<Switch size="small" checked={byPerson} onChange={(e) => setParam({ lanes: e.target.checked ? "person" : null })} />}
                                label={<Typography variant="body2">Row per person</Typography>}
                            />
                        ) : (
                            <TextField
                                select
                                size="small"
                                label="Group by"
                                value={groupBy}
                                onChange={(e) => setParam({ group: e.target.value === "status" ? null : e.target.value })}
                                sx={{ minWidth: 150 }}
                                slotProps={{ input: { sx: { borderRadius: 2, bgcolor: "background.paper" } } }}
                            >
                                {GROUP_OPTIONS.map((g) => (
                                    <MenuItem key={g.key} value={g.key}>
                                        {g.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Stack>

                    {/* People strip */}
                    {team.length > 1 && (
                        <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5 }}>
                            {team.map((p) => {
                                const selected = filters.person === p.user_id;
                                return (
                                    <Tooltip key={p.user_id} title={selected ? "Show everyone" : `Only ${personName(p)}'s tasks`}>
                                        <ButtonBase
                                            onClick={() => setFilters({ person: selected ? null : p.user_id })}
                                            sx={{
                                                flexShrink: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                pl: 0.5,
                                                pr: 1.5,
                                                py: 0.5,
                                                borderRadius: 99,
                                                border: "1px solid",
                                                borderColor: selected ? "#18383E" : "divider",
                                                bgcolor: selected ? "#e9f1ef" : "background.paper",
                                                "&:hover": { borderColor: "#18383E66" },
                                            }}
                                        >
                                            <StaffAvatar person={p} size={26} tooltip={false} />
                                            <Box sx={{ textAlign: "left" }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                                    {personName(p).split(" ")[0]}
                                                    {p.user_id === meId ? " (you)" : ""}
                                                </Typography>
                                                <Typography variant="caption" sx={{ lineHeight: 1.2, color: p.overdue_count ? "error.main" : "text.secondary" }}>
                                                    {p.open_count} open{p.overdue_count ? ` · ${p.overdue_count} late` : ""}
                                                </Typography>
                                            </Box>
                                        </ButtonBase>
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={() => store.reload()}>Retry</Button>}>
                    {error}
                </Alert>
            )}

            {loading && !tasks.length ? (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress />
                </Stack>
            ) : view === "my" ? (
                <MyTasks
                    sections={sections}
                    collapsed={collapsed}
                    onToggleSection={toggleCollapsed}
                    activeId={activeId}
                    onOpen={(id) => openTask(id)}
                    onOpenLink={viewer.open}
                    onNewTask={() => quickAddRef.current?.querySelector("input")?.focus()}
                    filtered={!!filters.q.trim()}
                />
            ) : view === "board" ? (
                <TaskBoard
                    tasks={filtered}
                    byPerson={byPerson}
                    activeId={activeId}
                    doneCollapsed={doneCollapsed}
                    onToggleDone={() => {
                        writeFlag(DONE_COLLAPSED_KEY, !doneCollapsed);
                        setDoneCollapsed(!doneCollapsed);
                    }}
                    onOpen={(id) => openTask(id)}
                    onOpenLink={viewer.open}
                    onAdd={(status: TaskStatus, assigneeId?: string | null) => setDialog({ status, assigneeId: assigneeId ?? null })}
                />
            ) : (
                <TaskList
                    groups={groups}
                    groupBy={groupBy}
                    sortBy={sortBy}
                    onSort={(s) => setParam({ sort: s === "due" ? null : s })}
                    collapsed={collapsed}
                    onToggleGroup={toggleCollapsed}
                    activeId={activeId}
                    onOpen={(id) => openTask(id)}
                    onOpenLink={viewer.open}
                />
            )}

            {view !== "my" && !loading && filtered.length === 0 && tasks.length > 0 && filtersOn && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
                    No tasks match these filters.{" "}
                    <Button size="small" onClick={() => setFilters(EMPTY_FILTERS)}>
                        Clear filters
                    </Button>
                </Typography>
            )}

            <TaskDrawer
                taskId={openTaskId}
                onClose={() => openTask(null)}
                hasPrev={openIndex > 0}
                hasNext={openIndex >= 0 && openIndex < orderedIds.length - 1}
                onStep={(d) => {
                    const next = orderedIds[openIndex + d];
                    if (next) setParam({ task: next });
                }}
            />

            <QuickAddDialog open={!!dialog} onClose={() => setDialog(null)} defaults={dialog || undefined} onCreated={onCreated} />

            {viewer.element}

            <Snackbar
                open={!!lastToast}
                autoHideDuration={lastToast?.severity === "error" ? 6000 : 3500}
                onClose={(_, reason) => reason !== "clickaway" && clearToast()}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {lastToast ? (
                    <Alert
                        severity={lastToast.severity || "info"}
                        variant="filled"
                        onClose={clearToast}
                        action={
                            lastToast.action ? (
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        lastToast.action!.onClick();
                                        clearToast();
                                    }}
                                >
                                    {lastToast.action.label}
                                </Button>
                            ) : undefined
                        }
                        sx={{ alignItems: "center" }}
                    >
                        {lastToast.message}
                    </Alert>
                ) : (
                    <span />
                )}
            </Snackbar>
        </Box>
    );
};
